import { useEffect, useRef, useState } from "react"

import { Button } from "@/components/ui/button"
import { MultiSelectDropdown } from "@/components/MultiSelectDropdown"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import type { UserFilterOption } from "@/api/hooks/offers"
import type { OffersDatePreset } from "../lib/presets"
import type { OffersState } from "../state/offersReducer"
import type { OfferColumn } from "../lib/columns"
import { DateRangePicker } from "@/components/DateRangePicker"

/**
 * Filter bar for the Offers page — layout aligned with Ads Manager /
 * Dashboard: dark panel row (filters left, dates + refresh right), search below.
 */

const PRESETS: Array<{ key: OffersDatePreset; label: string }> = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "thisweek", label: "This week" },
]

interface OfferFiltersProps {
  state: OffersState
  users: UserFilterOption[]
  currentUserRole?: string
  currentUserId?: number
  onStatusChange: (value: string) => void
  onUserIdsChange: (ids: number[]) => void
  onSearchChange: (value: string) => void
  onRefresh: () => void
  onPresetChange: (preset: OffersDatePreset) => void
  onCustomRangeApply: (
    startIso: string,
    endIso: string,
    displayLabel: string,
  ) => void
  onCreateOffer: () => void
  columns: readonly OfferColumn[]
  hiddenColumns: Set<string>
  onToggleColumn: (columnId: string) => void
}

export function OfferFilters({
  state,
  users,
  currentUserRole,
  currentUserId,
  onStatusChange,
  onUserIdsChange,
  onSearchChange,
  onRefresh,
  onPresetChange,
  onCustomRangeApply,
  onCreateOffer,
  columns,
  hiddenColumns,
  onToggleColumn,
}: OfferFiltersProps) {
  const [pickerOpen, setPickerOpen] = useState(false)
  const customRangeWrapRef = useRef<HTMLDivElement>(null)

  // Auto-select current user for non-admins. Parity: legacy offers/index.
  useEffect(() => {
    if (
      currentUserRole &&
      currentUserRole !== "admin" &&
      currentUserId &&
      state.selectedUserIds.length === 0 &&
      users.length > 0
    ) {
      const userExists = users.some((u) => u.id === currentUserId)
      if (userExists) {
        onUserIdsChange([currentUserId])
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users.length])

  const userOptions = users.map((u) => ({
    id: u.id,
    label: u.label || `${u.username} (${u.role})`,
  }))

  const selectedColumnIds = columns
    .filter((c) => !hiddenColumns.has(c.id))
    .map((c) => c.id)

  const handleColumnsChange = (nextSelectedIds: string[]) => {
    const prevSet = new Set(selectedColumnIds)
    const nextSet = new Set(nextSelectedIds)
    columns.forEach((col) => {
      const wasSelected = prevSet.has(col.id)
      const isSelected = nextSet.has(col.id)
      if (wasSelected !== isSelected) {
        onToggleColumn(col.id)
      }
    })
  }

  return (
    <>
      <div className="mb-3 rounded-lg border border-[#3f3f3f] bg-[#252525] px-2 py-2 sm:px-3">
        <div className="-mx-1 flex w-full min-w-0 flex-col gap-2 px-1 sm:flex-row sm:items-center sm:gap-2">
          <div className="-mx-1 flex min-w-0 flex-1 items-center gap-2 overflow-x-auto overscroll-x-contain px-1 py-0.5 [-webkit-overflow-scrolling:touch]">
            <div className="inline-flex min-w-max shrink-0 flex-nowrap items-center gap-1.5">
              <Button
                type="button"
                onClick={onCreateOffer}
                className="h-7 shrink-0 rounded-md bg-[#7488e8] px-3 text-xs font-medium text-white shadow-sm hover:bg-[#6378d8]"
              >
                + Create Offer
              </Button>

              <span className="shrink-0 text-[11px] font-semibold uppercase tracking-wider text-white">
                Filters
              </span>

              <Select value={state.status} onValueChange={onStatusChange}>
                <SelectTrigger className="h-7 w-[125px] shrink-0 border border-[#555] bg-[#2d2d2d] text-xs text-white focus:ring-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border-[#555] bg-[#2d2d2d] text-white">
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Attention">Attention</SelectItem>
                  <SelectItem value="Processing">Processing</SelectItem>
                  <SelectItem value="Failed">Failed</SelectItem>
                  <SelectItem value="Archived">Archived</SelectItem>
                </SelectContent>
              </Select>

              {users.length > 0 && (
                <MultiSelectDropdown
                  label="All Users"
                  options={userOptions}
                  selectedIds={state.selectedUserIds}
                  onChange={onUserIdsChange}
                  className="shrink-0"
                  minWidth="125px"
                  searchable
                  searchPlaceholder="Search users..."
                  showApplyClearActions
                />
              )}

              <MultiSelectDropdown
                label="Columns"
                options={columns.map((col) => ({ id: col.id, label: col.label }))}
                selectedIds={selectedColumnIds}
                onChange={handleColumnsChange}
                className="shrink-0"
                minWidth="110px"
              />
            </div>
          </div>

          <div className="flex shrink-0 flex-nowrap items-center justify-end gap-1.5 sm:ml-auto">
            <div
              className="mx-0.5 hidden h-6 w-px shrink-0 bg-[#3f3f3f] sm:block"
              aria-hidden
            />

            <button
              type="button"
              onClick={onRefresh}
              title="Refresh data"
              aria-label="Refresh data"
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-[#676d78] bg-[#4f5561] p-0 text-xs text-white shadow-sm transition-colors hover:bg-[#5a616f]"
            >
              <span aria-hidden>{"\uD83D\uDD04"}</span>
            </button>

            <div className="inline-flex shrink-0 flex-nowrap items-center gap-1">
              {PRESETS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => onPresetChange(p.key)}
                  className={`h-7 shrink-0 rounded-md border px-2.5 text-xs font-medium shadow-sm transition-colors ${
                    state.datePreset === p.key
                      ? "border-[#8fa0ef]/35 bg-[#7488e8] text-white"
                      : "border-[#555] bg-[#2d2d2d] text-white hover:bg-[#3a3a3a]"
                  }`}
                >
                  {p.label}
                </button>
              ))}
              <div ref={customRangeWrapRef} className="relative shrink-0">
                <button
                  type="button"
                  onClick={() => setPickerOpen((v) => !v)}
                  className="flex h-7 max-w-[120px] items-center gap-1.5 rounded-md border border-[#555] bg-[#2d2d2d] px-2.5 text-xs font-medium text-white shadow-sm transition-colors hover:bg-[#3a3a3a] sm:max-w-[140px]"
                  aria-expanded={pickerOpen}
                  aria-haspopup="dialog"
                  aria-label="Custom date range"
                >
                  <span className="truncate">{state.dateDisplayLabel}</span>
                  <span className="text-[0.7rem]" aria-hidden>
                    &#9660;
                  </span>
                </button>
                <DateRangePicker
                  open={pickerOpen}
                  onClose={() => setPickerOpen(false)}
                  initialStart={state.startDate || null}
                  initialEnd={state.endDate || null}
                  outsideBoundaryRef={customRangeWrapRef}
                  onApply={(s, e, label) => {
                    onCustomRangeApply(s, e, label)
                    setPickerOpen(false)
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <input
        type="text"
        value={state.search}
        onChange={(ev) => onSearchChange(ev.target.value)}
        placeholder={"Search offers by name or status\u2026"}
        className="mb-4 w-full rounded border border-[#4a4a4a] bg-[#2d2d2d] px-3 py-2 text-xs text-white placeholder-white/50 focus:border-[#8b9aff] focus:outline-none"
      />
    </>
  )
}
